import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import MontserratRegular from '../style/fonts/Montserrat/static/Montserrat-Regular.ttf';
import MontserratMedium from '../style/fonts/Montserrat/static/Montserrat-Medium.ttf';
import MontserratSemiBold from '../style/fonts/Montserrat/static/Montserrat-SemiBold.ttf';
import MontserratBold from '../style/fonts/Montserrat/static/Montserrat-Bold.ttf';
import MontserratExtraBold from '../style/fonts/Montserrat/static/Montserrat-ExtraBold.ttf';

const features = [
  {
    title: 'Created for building brands!',
    points: [
      'Premium-grade blanks.',
      'Large size Direct to Garment (DTG) printing',
      'Custom packaging and branding.',
    ],
    image: '/home2.png',
  },
  {
    title: 'We handle everything!',
    points: [
      'Next Day dispatch!',
      'Hassle-free returns management',
      'Connect your Shopify store in 2 clicks',
      'Fast COD payouts',
    ],
    image: '/home3.png',
  },
  {
    title: 'Go live in minutes.',
    points: [
      'No MOQ',
      'Pay only when you sell',
      '100% automated, from your website to your customer’s doorstep',
    ],
    image: '/home4.png',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @font-face {
        font-family: 'Montserrat';
        src: url(${MontserratRegular}) format('truetype');
        font-weight: 400;
        font-display: swap;
      }
      @font-face {
        font-family: 'Montserrat';
        src: url(${MontserratMedium}) format('truetype');
        font-weight: 500;
        font-display: swap;
      }
      @font-face {
        font-family: 'Montserrat';
        src: url(${MontserratSemiBold}) format('truetype');
        font-weight: 600;
        font-display: swap;
      }
      @font-face {
        font-family: 'Montserrat';
        src: url(${MontserratBold}) format('truetype');
        font-weight: 700;
        font-display: swap;
      }
      @font-face {
        font-family: 'Montserrat';
        src: url(${MontserratExtraBold}) format('truetype');
        font-weight: 800;
        font-display: swap;
      }

      * {
        font-family: 'Montserrat', sans-serif;
      }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <div className="bg-white text-black min-h-screen">
      {/* Header */}
      <header className="fixed top-0 w-full z-50">
        <div className="backdrop-blur-md bg-black/80 text-white flex items-center justify-between px-6 sm:px-8 lg:px-24 py-5 shadow-lg">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
          </div>
          {/* Desktop Menu */}
          <div className="hidden md:flex gap-6">
            <button onClick={() => navigate('/')} className="hover:text-gray-300 transition font-medium">
              Login
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="px-5 py-2 bg-white text-black font-semibold rounded hover:opacity-90 transition"
            >
              Sign Up
            </button>
          </div>

          {/* Hamburger Menu */}
          <div className="md:hidden">
            <button onClick={() => setMenuOpen(!menuOpen)}>
              <div className="space-y-1">
                <span className="block w-6 h-0.5 bg-white"></span>
                <span className="block w-6 h-0.5 bg-white"></span>
                <span className="block w-6 h-0.5 bg-white"></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-black/90 backdrop-blur-md text-white px-6 py-4 md:hidden space-y-4"
            >
              <button
                onClick={() => {
                  navigate('/');
                  setMenuOpen(false);
                }}
                className="block w-full text-left text-base"
              >
                Login
              </button>
              <button
                onClick={() => {
                  navigate('/signup');
                  setMenuOpen(false);
                }}
                className="block w-full text-left text-base"
              >
                Sign Up
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Hero + Features */}
      <main className="pt-36 pb-24 space-y-28 px-6 sm:px-8 lg:px-24">
        {/* Hero Section */}
        <motion.section
          className="flex flex-col-reverse lg:flex-row items-center gap-14 lg:gap-20"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <div className="w-full lg:w-1/2 text-center lg:text-left space-y-4">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight md:text-left">
              You build the brand,<br /> we run the engine.
            </h1>
            <p className="text-gray-700 text-xl max-w-xl mx-auto lg:mx-0 font-medium">
              From production to delivery, we handle everything while you scale your brand.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/')}
              className="mt-2 px-6 py-3 bg-black text-white text-lg rounded-md shadow hover:opacity-90 transition"
            >
              Get started →
            </motion.button>
          </div>
          <div className="w-full lg:w-1/2">
            <img
              src="/home1.png"
              alt="Hero"
              className="w-full h-auto rounded-xl shadow-xl object-cover"
            />
          </div>
        </motion.section>

        {/* Feature Sections */}
        {features.map((feature, idx) => (
          <motion.section
            key={idx}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: idx * 0.2 }}
            viewport={{ once: true }}
            className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-14 lg:gap-20`}
          >
            <div className="w-full lg:w-1/2">
              <img
                src={feature.image}
                alt={feature.title}
                className="w-full h-auto rounded-xl object-cover shadow-md"
              />
            </div>
            <div className="w-full lg:w-1/2 space-y-6 text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl font-extrabold">{feature.title}</h2>
              <ul className="space-y-4 text-left text-[1.15rem] font-medium max-w-md mx-auto lg:mx-0">
                {feature.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-green-600 mt-1 text-xl">✔</span>
                    <span className="text-gray-900">{point}</span>
                  </li>
                ))}
              </ul>
              <motion.button
                whileHover={{ scale: 1.04 }}
                className="mt-2 px-5 py-2 border border-black text-base font-semibold rounded hover:bg-black hover:text-white transition"
              >
                Learn more
              </motion.button>
            </div>
          </motion.section>
        ))}
      </main>
    </div>
  );
}